// ComplianceHub.js v1.0 - Compliance Hub for Contractor Portal
// Sub-tabs: Requirements, Report/Request, Documents
// Forms auto-generate PDFs and auto-email to hardcoded recipients (invisible to subs)
import React, { useState, useCallback } from 'react';
import { Shield, ChevronDown, ChevronUp, AlertTriangle, FileText, ClipboardList, MapPin, Package, Loader, CheckCircle, Info } from 'lucide-react';
import { getTheme, getCardStyle, getInputStyle, getButtonStyle, useMobile } from '../../utils/theme';
import { createFormPdf } from '../../services/pdfService';
import { sendDamageReportEmail, sendPotholeVerificationEmail, sendMaterialRequestEmail } from '../../services/notificationService';
import { fetchWithRedirect } from '../../utils/fetchHelpers';
import { isGpsEnabled } from '../../services/gpsService';

const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const OPERATIONS_SHEET_ID = '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc';

const submitToSheet = async (sheetName, rowData) => {
  try {
    const text = await fetchWithRedirect(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsAppend',
        params: {
          spreadsheetId: OPERATIONS_SHEET_ID,
          range: `${sheetName}!A:Z`,
          values: [rowData]
        }
      })
    });
    const result = JSON.parse(text);
    return result.success;
  } catch (err) {
    console.error('Submit error:', err);
    return false;
  }
};

// ========== REQUIREMENTS DATA ==========
const REQUIREMENTS = [
  {
    id: 'safety-training',
    title: 'Safety Training & Certifications',
    summary: 'OSHA 10/30, flagger cert, and project-specific safety orientation required before any field work.',
    priority: 'Critical',
    rules: [
      'All crew members must have current OSHA 10 or OSHA 30 certification',
      'Flagger certification required for any traffic control work',
      'Project-specific safety orientation must be completed before starting',
      'Toolbox talks must be conducted and documented daily',
      'All safety incidents must be reported within 1 hour',
    ],
  },
  {
    id: 'insurance-bonding',
    title: 'Insurance & Bonding',
    summary: 'Maintain current COI with minimum coverage limits. LYT must be listed as additional insured.',
    priority: 'Critical',
    rules: [
      'General Liability: minimum $1M per occurrence / $2M aggregate',
      'Auto Liability: minimum $1M combined single limit',
      'Workers Compensation: statutory limits for your state',
      'LYT Communications, LLC must be listed as Additional Insured on all policies',
      'Updated COI must be on file before starting work — expired COI = immediate stand-down',
      'Notify LYT within 24 hours of any policy changes, cancellations, or renewals',
    ],
  },
  {
    id: 'damage-reporting',
    title: 'Damage & Utility Strike Reporting',
    summary: 'All utility strikes and property damage must be reported immediately with full documentation.',
    priority: 'Critical',
    rules: [
      'ALL utility strikes must be reported within 15 minutes — no exceptions',
      'Complete the Damage Report form (Report/Request tab) with all required fields',
      'Photograph the damage from multiple angles before any repair attempts',
      'Do NOT attempt to repair utility damage yourself — call 811 and wait for the utility owner',
      'If homes/businesses are affected, stay on-site until utility company arrives',
      'Contact the city if any public infrastructure is damaged',
    ],
    actionLabel: 'File Damage Report',
    actionTarget: 'damage',
  },
  {
    id: 'potholing',
    title: 'Potholing & Utility Verification',
    summary: 'Pothole at every crossing. Document utility location, depth, and direction before boring.',
    priority: 'Critical',
    rules: [
      'Pothole at EVERY utility crossing — no assumptions about utility depth',
      'Record: utility type, depth, direction, and surface type',
      'Pothole verification must be submitted before boring through that crossing',
      'Use vacuum excavation or hand dig only — no mechanical excavation near utilities',
      'If utility is not found at expected location, expand search area and document',
      'GPS coordinates required for all pothole locations',
    ],
    actionLabel: 'Submit Pothole Verification',
    actionTarget: 'pothole',
  },
  {
    id: 'locate-tickets',
    title: 'Locate Tickets & 811',
    summary: 'Valid locate ticket required before any excavation. Locates expire after 14 calendar days.',
    priority: 'Critical',
    rules: [
      'Call 811 minimum 48 hours before any excavation work',
      'Valid locate ticket must be on file before breaking ground',
      'Locate tickets expire after 14 calendar days — renew before expiration',
      'White-line the dig area before locators arrive',
      'Verify all locate marks are present and match expected utilities',
      'If locates are missing or unclear, call for re-mark before proceeding',
    ],
  },
  {
    id: 'daily-documentation',
    title: 'Daily Documentation & Reporting',
    summary: 'Daily production reports, crew counts, and progress photos required by end of each work day.',
    priority: 'High',
    rules: [
      'Submit daily production report by 6:00 PM each work day',
      'Include: crew count, equipment used, footage completed, work type',
      'Progress photos required for each segment worked',
      'Document any delays, issues, or deviations from plan',
      'Street sheets must be completed for every street worked',
    ],
  },
  {
    id: 'material-handling',
    title: 'Material Management',
    summary: 'All materials must be tracked, stored properly, and requested through LYT.',
    priority: 'High',
    rules: [
      'All material requests must go through LYT — do not order directly from suppliers',
      'Use the Material Request form (Report/Request tab) for all requests',
      'Store conduit and fiber on racks or pallets — never on bare ground',
      'Track material usage per segment in daily production reports',
      'Report any damaged or defective materials immediately',
      'Standard fulfillment is 48 hours — plan ahead for large orders',
    ],
    actionLabel: 'Submit Material Request',
    actionTarget: 'material',
  },
  {
    id: 'traffic-control',
    title: 'Traffic Control & MOT',
    summary: 'MUTCD-compliant traffic control required. MOT plans must be approved before work begins.',
    priority: 'High',
    rules: [
      'All traffic control must comply with MUTCD standards',
      'Maintenance of Traffic (MOT) plan required before starting any road work',
      'Certified flaggers required at all active road crossings',
      'Cones, signs, and barricades must be in good condition — replace damaged items immediately',
      'Remove all traffic control devices at end of each work day unless continuous operation',
      'High-visibility vests required for all personnel in or near roadways',
    ],
  },
  {
    id: 'restoration',
    title: 'Restoration Requirements',
    summary: 'All disturbed areas must be restored to pre-construction condition within 48 hours.',
    priority: 'High',
    rules: [
      'Restore all disturbed areas within 48 hours of completing work in that area',
      'Concrete: color-match, saw-cut edges, minimum 4" depth, 3000 PSI',
      'Asphalt: tack coat required, compact to match existing grade',
      'Sod/grass: install sod within 48 hours, water for establishment',
      'Driveways must be passable at end of each work day (steel plates if needed)',
      'Take before and after photos of every restoration',
    ],
  },
  {
    id: 'equipment-vehicles',
    title: 'Equipment & Vehicle Standards',
    summary: 'All equipment must be inspected daily. Vehicles must display company identification.',
    priority: 'Medium',
    rules: [
      'Daily pre-trip inspection required for all vehicles and equipment',
      'All vehicles must display company name and DOT number (if applicable)',
      'Equipment must have current inspection stickers where required',
      'No leaking fluids — repair or remove from service immediately',
      'Fire extinguisher required on every piece of equipment',
      'Park equipment off roadways when not in active use',
    ],
  },
  {
    id: 'environmental',
    title: 'Environmental Compliance',
    summary: 'Prevent erosion and contamination. Spill kits required on all job sites.',
    priority: 'Medium',
    rules: [
      'Spill kits required on every job site — respond to spills immediately',
      'Install silt fencing and erosion control before excavation near waterways',
      'No dumping of any materials — dispose of waste at approved facilities',
      'Dewatering requires proper filtration before discharge',
      'Report any environmental concerns (contaminated soil, unknown substances) immediately',
    ],
  },
];

const PRIORITY_COLORS = {
  Critical: { bg: '#dc354520', fg: '#dc3545', border: '#dc3545' },
  High: { bg: '#fd7e1420', fg: '#fd7e14', border: '#fd7e14' },
  Medium: { bg: '#0d6efd20', fg: '#0d6efd', border: '#0d6efd' },
};

// ========== COMPONENT ==========
const ComplianceHub = ({
  darkMode,
  loggedInUser,
  complianceItems,
  complianceLoading,
  loadComplianceData,
}) => {
  const theme = getTheme(darkMode);
  const { colors: tc } = theme;
  const isMobile = useMobile();
  const cardStyle = { ...getCardStyle(darkMode), padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' };
  const inputStyle = getInputStyle(darkMode);

  // Sub-tab state
  const [subTab, setSubTab] = useState('requirements');

  // Accordion state
  const [expandedReqs, setExpandedReqs] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  // Form states
  const [damageForm, setDamageForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    project: '', jbNumber: '', location: '', gpsLat: '', gpsLng: '',
    crewLead: '', phone: '',
    utilityType: '', serviceOrMain: '', markedOrUnmarked: '', utilityOwner: '',
    description: '', immediateActions: '', potholedPrior: '', utilityLocated: '',
    potholingMethod: '', homesAffected: '', cityContacted: '', photosTaken: '',
    digTicketNumber: '', lobManager: '',
  });
  const [potholeForm, setPotholeForm] = useState({
    project: '', address: '', crossStreet: '', gpsLat: '', gpsLng: '',
    depth: '', utilityFound: '', utilityType: '', utilityDepth: '', utilityDirection: '',
    surfaceType: '', crewLead: '', photosTaken: '', notes: '',
  });
  const [materialForm, setMaterialForm] = useState({
    project: '', materialType: '', description: '', qty: '', unit: 'ft',
    deliveryLocation: '', urgency: 'Standard', neededByDate: '',
  });

  const [formLoading, setFormLoading] = useState(false);
  const [formStatus, setFormStatus] = useState({ show: false, success: false, message: '' });
  const [activeForm, setActiveForm] = useState(null); // 'damage' | 'pothole' | 'material' | null
  const [gpsLoading, setGpsLoading] = useState(false);

  // GPS capture
  const captureGPS = useCallback((formSetter) => {
    if (!navigator.geolocation || !isGpsEnabled()) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        formSetter(prev => ({
          ...prev,
          gpsLat: pos.coords.latitude.toFixed(6),
          gpsLng: pos.coords.longitude.toFixed(6),
        }));
        setGpsLoading(false);
      },
      (err) => {
        console.error('GPS error:', err);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Toggle accordion
  const toggleReq = (id) => {
    setExpandedReqs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    const newState = !allExpanded;
    setAllExpanded(newState);
    const map = {};
    REQUIREMENTS.forEach(r => { map[r.id] = newState; });
    setExpandedReqs(map);
  };

  // Form toast
  const showToast = (success, message) => {
    setFormStatus({ show: true, success, message });
    setTimeout(() => setFormStatus({ show: false, success: false, message: '' }), 5000);
  };

  // ========== DAMAGE REPORT SUBMIT ==========
  const submitDamageReport = async () => {
    if (!damageForm.date || !damageForm.jbNumber || !damageForm.location || !damageForm.crewLead || !damageForm.utilityType) {
      showToast(false, 'Please fill in all required fields (date, JB number, location, crew lead, utility type)');
      return;
    }
    setFormLoading(true);
    try {
      // Generate PDF
      const pdfContent = [
        {
          title: 'Incident Details',
          fields: [
            { label: 'Date', value: damageForm.date },
            { label: 'Time', value: damageForm.time },
            { label: 'Project', value: damageForm.project },
            { label: 'JB Number', value: damageForm.jbNumber },
            { label: 'Location', value: damageForm.location },
            { label: 'GPS', value: damageForm.gpsLat && damageForm.gpsLng ? `${damageForm.gpsLat}, ${damageForm.gpsLng}` : 'Not captured' },
            { label: 'Crew Lead', value: damageForm.crewLead },
            { label: 'Phone', value: damageForm.phone },
          ],
        },
        {
          title: 'Utility Information',
          fields: [
            { label: 'Utility Type Hit', value: damageForm.utilityType },
            { label: 'Service or Main', value: damageForm.serviceOrMain },
            { label: 'Marked or Unmarked', value: damageForm.markedOrUnmarked },
            { label: 'Utility Owner', value: damageForm.utilityOwner },
            { label: 'Original Dig Ticket #', value: damageForm.digTicketNumber },
            { label: 'LOB Manager', value: damageForm.lobManager },
          ],
        },
        {
          title: 'Actions & Assessment',
          fields: [
            { label: 'Description', value: damageForm.description },
            { label: 'Immediate Actions Taken', value: damageForm.immediateActions },
            { label: 'Potholed Prior?', value: damageForm.potholedPrior },
            { label: 'Utility Located?', value: damageForm.utilityLocated },
            { label: 'Potholing Method', value: damageForm.potholingMethod },
            { label: 'Homes/Businesses Affected', value: damageForm.homesAffected },
            { label: 'City Contacted?', value: damageForm.cityContacted },
            { label: 'Photos Taken?', value: damageForm.photosTaken },
          ],
        },
      ];
      const pdfDataUrl = await createFormPdf('Damage / Utility Strike Report', pdfContent);

      // Submit to Google Sheet
      const sheetRow = [
        new Date().toISOString(),
        loggedInUser?.email || '',
        loggedInUser?.company || loggedInUser?.name || '',
        damageForm.date, damageForm.time, damageForm.project, damageForm.jbNumber,
        damageForm.location, `${damageForm.gpsLat}, ${damageForm.gpsLng}`,
        damageForm.crewLead, damageForm.phone,
        damageForm.utilityType, damageForm.serviceOrMain, damageForm.markedOrUnmarked,
        damageForm.utilityOwner, damageForm.description, damageForm.immediateActions,
        damageForm.potholedPrior, damageForm.utilityLocated, damageForm.potholingMethod,
        damageForm.homesAffected, damageForm.cityContacted, damageForm.photosTaken,
        damageForm.digTicketNumber, damageForm.lobManager,
      ];
      await submitToSheet('Damage Reports', sheetRow);

      // Send email with PDF (routing handled in notificationService)
      await sendDamageReportEmail(damageForm, loggedInUser, pdfDataUrl);

      showToast(true, 'Damage report submitted and sent to management');
      setDamageForm({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        project: '', jbNumber: '', location: '', gpsLat: '', gpsLng: '',
        crewLead: '', phone: '',
        utilityType: '', serviceOrMain: '', markedOrUnmarked: '', utilityOwner: '',
        description: '', immediateActions: '', potholedPrior: '', utilityLocated: '',
        potholingMethod: '', homesAffected: '', cityContacted: '', photosTaken: '',
        digTicketNumber: '', lobManager: '',
      });
      setActiveForm(null);
    } catch (err) {
      console.error('Damage report error:', err);
      showToast(false, 'Failed to submit damage report. Please try again.');
    }
    setFormLoading(false);
  };

  // ========== POTHOLE VERIFICATION SUBMIT ==========
  const submitPotholeVerification = async () => {
    if (!potholeForm.project || !potholeForm.address || !potholeForm.crewLead) {
      showToast(false, 'Please fill in all required fields (project, address, crew lead)');
      return;
    }
    setFormLoading(true);
    try {
      const pdfContent = [
        {
          title: 'Location',
          fields: [
            { label: 'Project', value: potholeForm.project },
            { label: 'Address', value: potholeForm.address },
            { label: 'Cross Street', value: potholeForm.crossStreet },
            { label: 'GPS', value: potholeForm.gpsLat && potholeForm.gpsLng ? `${potholeForm.gpsLat}, ${potholeForm.gpsLng}` : 'Not captured' },
            { label: 'Surface Type', value: potholeForm.surfaceType },
          ],
        },
        {
          title: 'Findings',
          fields: [
            { label: 'Depth', value: potholeForm.depth },
            { label: 'Utility Found', value: potholeForm.utilityFound },
            ...(potholeForm.utilityFound === 'Yes' ? [
              { label: 'Utility Type', value: potholeForm.utilityType },
              { label: 'Utility Depth', value: potholeForm.utilityDepth },
              { label: 'Utility Direction', value: potholeForm.utilityDirection },
            ] : []),
            { label: 'Photos Taken', value: potholeForm.photosTaken },
            { label: 'Crew Lead', value: potholeForm.crewLead },
          ],
        },
        ...(potholeForm.notes ? [{
          title: 'Notes',
          paragraphs: [potholeForm.notes],
        }] : []),
      ];
      const pdfDataUrl = await createFormPdf('Pothole Verification Report', pdfContent);

      const sheetRow = [
        new Date().toISOString(),
        loggedInUser?.email || '',
        loggedInUser?.company || loggedInUser?.name || '',
        potholeForm.project, potholeForm.address, potholeForm.crossStreet,
        `${potholeForm.gpsLat}, ${potholeForm.gpsLng}`,
        potholeForm.depth, potholeForm.utilityFound,
        potholeForm.utilityType, potholeForm.utilityDepth, potholeForm.utilityDirection,
        potholeForm.surfaceType, potholeForm.crewLead, potholeForm.photosTaken, potholeForm.notes,
      ];
      await submitToSheet('Pothole Verifications', sheetRow);

      await sendPotholeVerificationEmail(potholeForm, loggedInUser, pdfDataUrl);

      showToast(true, 'Pothole verification submitted');
      setPotholeForm({
        project: '', address: '', crossStreet: '', gpsLat: '', gpsLng: '',
        depth: '', utilityFound: '', utilityType: '', utilityDepth: '', utilityDirection: '',
        surfaceType: '', crewLead: '', photosTaken: '', notes: '',
      });
      setActiveForm(null);
    } catch (err) {
      console.error('Pothole verification error:', err);
      showToast(false, 'Failed to submit pothole verification. Please try again.');
    }
    setFormLoading(false);
  };

  // ========== MATERIAL REQUEST SUBMIT ==========
  const submitMaterialRequest = async () => {
    if (!materialForm.project || !materialForm.materialType || !materialForm.qty) {
      showToast(false, 'Please fill in all required fields (project, material type, quantity)');
      return;
    }
    setFormLoading(true);
    try {
      const pdfContent = [
        {
          title: 'Request Details',
          fields: [
            { label: 'Project', value: materialForm.project },
            { label: 'Material Type', value: materialForm.materialType },
            { label: 'Description / Specs', value: materialForm.description },
            { label: 'Quantity', value: `${materialForm.qty} ${materialForm.unit}` },
            { label: 'Delivery Location', value: materialForm.deliveryLocation },
            { label: 'Urgency', value: materialForm.urgency },
            { label: 'Needed By', value: materialForm.neededByDate },
          ],
        },
      ];
      const pdfDataUrl = await createFormPdf('Material Request', pdfContent);

      const sheetRow = [
        new Date().toISOString(),
        loggedInUser?.email || '',
        loggedInUser?.company || loggedInUser?.name || '',
        materialForm.project, materialForm.materialType, materialForm.description,
        materialForm.qty, materialForm.unit, materialForm.deliveryLocation,
        materialForm.urgency, materialForm.neededByDate,
      ];
      await submitToSheet('Material Requests', sheetRow);

      await sendMaterialRequestEmail(materialForm, loggedInUser, pdfDataUrl);

      showToast(true, 'Material request submitted — standard fulfillment is 48 hours');
      setMaterialForm({
        project: '', materialType: '', description: '', qty: '', unit: 'ft',
        deliveryLocation: '', urgency: 'Standard', neededByDate: '',
      });
      setActiveForm(null);
    } catch (err) {
      console.error('Material request error:', err);
      showToast(false, 'Failed to submit material request. Please try again.');
    }
    setFormLoading(false);
  };

  // ========== FORM INPUT HELPERS ==========
  const formInputStyle = {
    ...inputStyle,
    marginBottom: '12px',
  };

  const formLabelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: tc.text,
    marginBottom: '4px',
  };

  const formSelectStyle = {
    ...formInputStyle,
    appearance: 'auto',
  };

  const formTextareaStyle = {
    ...formInputStyle,
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit',
  };

  const requiredStar = { color: tc.error, marginLeft: '2px' };

  // ========== SUB-TAB PILLS ==========
  const renderSubTabs = () => {
    const tabs = [
      { id: 'requirements', label: 'Requirements', icon: ClipboardList },
      { id: 'forms', label: 'Report / Request', icon: FileText },
      { id: 'documents', label: 'Documents', icon: Shield },
    ];

    return (
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '24px',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '4px',
      }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = subTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: '20px',
                fontSize: '0.85rem', fontWeight: 600,
                border: isActive ? 'none' : `1px solid ${tc.border}`,
                background: isActive ? tc.gradientPortal : 'transparent',
                color: isActive ? '#fff' : tc.textMuted,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  };

  // ========== REQUIREMENTS ACCORDION ==========
  const renderRequirements = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h3 style={{ margin: 0, color: tc.text, fontSize: '1.1rem' }}>Operational Requirements</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: tc.textMuted }}>
            {REQUIREMENTS.length} categories — review all requirements before starting work
          </p>
        </div>
        <button
          onClick={toggleAll}
          style={{
            ...getButtonStyle('ghost', darkMode),
            padding: '6px 12px', fontSize: '0.8rem',
            color: tc.accentPrimary,
          }}
        >
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {REQUIREMENTS.map((req) => {
          const isOpen = expandedReqs[req.id];
          const pc = PRIORITY_COLORS[req.priority];
          return (
            <div key={req.id} style={{
              ...cardStyle,
              padding: 0,
              overflow: 'hidden',
              borderLeft: `3px solid ${pc.border}`,
            }}>
              {/* Header — always visible */}
              <button
                onClick={() => toggleReq(req.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                  textAlign: 'left', gap: '12px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700,
                      backgroundColor: pc.bg, color: pc.fg,
                    }}>
                      {req.priority}
                    </span>
                    <span style={{ fontWeight: 600, color: tc.text, fontSize: '0.95rem' }}>{req.title}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: tc.textMuted, lineHeight: 1.4 }}>{req.summary}</p>
                </div>
                {isOpen ? <ChevronUp size={18} color={tc.textMuted} /> : <ChevronDown size={18} color={tc.textMuted} />}
              </button>

              {/* Expanded content */}
              {isOpen && (
                <div style={{
                  padding: '0 20px 16px',
                  borderTop: `1px solid ${tc.border}`,
                }}>
                  <ul style={{
                    margin: '12px 0 0', paddingLeft: '20px',
                    listStyleType: 'disc', lineHeight: 1.8,
                  }}>
                    {req.rules.map((rule, i) => (
                      <li key={i} style={{ fontSize: '0.85rem', color: tc.text, marginBottom: '4px' }}>{rule}</li>
                    ))}
                  </ul>
                  {req.actionLabel && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSubTab('forms'); setActiveForm(req.actionTarget); }}
                      style={{
                        ...getButtonStyle('primary', darkMode),
                        marginTop: '12px',
                        padding: '8px 16px',
                        fontSize: '0.82rem',
                        background: tc.gradientPortal,
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                      }}
                    >
                      <FileText size={14} />
                      {req.actionLabel}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ========== FORMS SECTION ==========
  const renderForms = () => {
    const formCards = [
      {
        id: 'damage',
        title: 'Damage / Utility Strike Report',
        icon: AlertTriangle,
        color: '#dc3545',
        desc: 'Report any utility strike or property damage immediately',
      },
      {
        id: 'pothole',
        title: 'Pothole Verification',
        icon: MapPin,
        color: '#fd7e14',
        desc: 'Document utility verification at bore crossings',
      },
      {
        id: 'material',
        title: 'Material Request',
        icon: Package,
        color: '#0d6efd',
        desc: 'Request materials for your crew — 48-hour standard fulfillment',
      },
    ];

    return (
      <div>
        <h3 style={{ margin: '0 0 4px', color: tc.text, fontSize: '1.1rem' }}>Report / Request</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: tc.textMuted }}>
          Fill out the form, hit submit — we handle the rest
        </p>

        {/* Form selector cards */}
        {!activeForm && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '12px', marginBottom: '16px',
          }}>
            {formCards.map(fc => {
              const Icon = fc.icon;
              return (
                <button
                  key={fc.id}
                  onClick={() => setActiveForm(fc.id)}
                  style={{
                    ...cardStyle,
                    padding: '20px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    border: `1px solid ${tc.border}`,
                    borderTop: `3px solid ${fc.color}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                >
                  <Icon size={28} color={fc.color} style={{ marginBottom: '10px' }} />
                  <div style={{ fontWeight: 600, color: tc.text, fontSize: '0.95rem', marginBottom: '4px' }}>{fc.title}</div>
                  <div style={{ fontSize: '0.82rem', color: tc.textMuted }}>{fc.desc}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Active form */}
        {activeForm === 'damage' && renderDamageForm()}
        {activeForm === 'pothole' && renderPotholeForm()}
        {activeForm === 'material' && renderMaterialForm()}
      </div>
    );
  };

  // ========== DAMAGE FORM ==========
  const renderDamageForm = () => (
    <div style={{ ...cardStyle, borderTop: '3px solid #dc3545' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertTriangle size={22} color="#dc3545" />
          <h3 style={{ margin: 0, color: tc.text }}>Damage / Utility Strike Report</h3>
        </div>
        <button onClick={() => setActiveForm(null)} style={{ background: 'none', border: 'none', color: tc.textMuted, cursor: 'pointer', fontSize: '0.85rem' }}>
          Back
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
        {/* Date & Time */}
        <div>
          <label style={formLabelStyle}>Date<span style={requiredStar}>*</span></label>
          <input type="date" value={damageForm.date} onChange={e => setDamageForm(p => ({ ...p, date: e.target.value }))} style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Time</label>
          <input type="time" value={damageForm.time} onChange={e => setDamageForm(p => ({ ...p, time: e.target.value }))} style={formInputStyle} />
        </div>

        {/* Project & JB Number */}
        <div>
          <label style={formLabelStyle}>Project</label>
          <input value={damageForm.project} onChange={e => setDamageForm(p => ({ ...p, project: e.target.value }))} placeholder="e.g. Bastrop Fiber" style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>JB Number<span style={requiredStar}>*</span></label>
          <input value={damageForm.jbNumber} onChange={e => setDamageForm(p => ({ ...p, jbNumber: e.target.value }))} placeholder="e.g. HLBPEA2129" style={formInputStyle} />
        </div>

        {/* Location */}
        <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
          <label style={formLabelStyle}>Location<span style={requiredStar}>*</span></label>
          <input value={damageForm.location} onChange={e => setDamageForm(p => ({ ...p, location: e.target.value }))} placeholder="Street address or intersection" style={formInputStyle} />
        </div>

        {/* GPS */}
        <div>
          <label style={formLabelStyle}>GPS Coordinates</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input value={damageForm.gpsLat && damageForm.gpsLng ? `${damageForm.gpsLat}, ${damageForm.gpsLng}` : ''} readOnly placeholder="Auto-capture" style={{ ...formInputStyle, flex: 1 }} />
            <button onClick={() => captureGPS(setDamageForm)} disabled={gpsLoading} style={{ ...getButtonStyle('outline', darkMode), padding: '8px 12px', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
              {gpsLoading ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <MapPin size={14} />}
            </button>
          </div>
        </div>

        {/* Crew Lead & Phone */}
        <div>
          <label style={formLabelStyle}>Crew Lead<span style={requiredStar}>*</span></label>
          <input value={damageForm.crewLead} onChange={e => setDamageForm(p => ({ ...p, crewLead: e.target.value }))} style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Phone</label>
          <input type="tel" value={damageForm.phone} onChange={e => setDamageForm(p => ({ ...p, phone: e.target.value }))} style={formInputStyle} />
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: tc.border, margin: '12px 0 16px' }} />
      <h4 style={{ margin: '0 0 12px', color: tc.text, fontSize: '0.95rem' }}>Utility Information</h4>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
        <div>
          <label style={formLabelStyle}>Utility Type Hit<span style={requiredStar}>*</span></label>
          <select value={damageForm.utilityType} onChange={e => setDamageForm(p => ({ ...p, utilityType: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Gas">Gas</option>
            <option value="Electric">Electric</option>
            <option value="Water">Water</option>
            <option value="Sewer">Sewer</option>
            <option value="Telecom">Telecom</option>
            <option value="Fiber">Fiber</option>
            <option value="Cable TV">Cable TV</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>Service or Main?</label>
          <select value={damageForm.serviceOrMain} onChange={e => setDamageForm(p => ({ ...p, serviceOrMain: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Service">Service</option>
            <option value="Main">Main</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>Marked or Unmarked?</label>
          <select value={damageForm.markedOrUnmarked} onChange={e => setDamageForm(p => ({ ...p, markedOrUnmarked: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Marked">Marked</option>
            <option value="Unmarked">Unmarked</option>
            <option value="Mismarked">Mismarked</option>
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>Utility Owner (who does the line belong to?)</label>
          <input value={damageForm.utilityOwner} onChange={e => setDamageForm(p => ({ ...p, utilityOwner: e.target.value }))} placeholder="e.g. Atmos Energy" style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Original Dig Ticket Number</label>
          <input value={damageForm.digTicketNumber} onChange={e => setDamageForm(p => ({ ...p, digTicketNumber: e.target.value }))} style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>LOB Manager Who Issued Work</label>
          <input value={damageForm.lobManager} onChange={e => setDamageForm(p => ({ ...p, lobManager: e.target.value }))} style={formInputStyle} />
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: tc.border, margin: '12px 0 16px' }} />
      <h4 style={{ margin: '0 0 12px', color: tc.text, fontSize: '0.95rem' }}>Actions & Assessment</h4>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
        <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
          <label style={formLabelStyle}>Description of Damage</label>
          <textarea value={damageForm.description} onChange={e => setDamageForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe what happened..." style={formTextareaStyle} />
        </div>
        <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
          <label style={formLabelStyle}>Immediate Actions Taken</label>
          <textarea value={damageForm.immediateActions} onChange={e => setDamageForm(p => ({ ...p, immediateActions: e.target.value }))} placeholder="What did you do?" style={formTextareaStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Was potholing performed prior?</label>
          <select value={damageForm.potholedPrior} onChange={e => setDamageForm(p => ({ ...p, potholedPrior: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>Was the utility located?</label>
          <select value={damageForm.utilityLocated} onChange={e => setDamageForm(p => ({ ...p, utilityLocated: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>Potholing method used</label>
          <select value={damageForm.potholingMethod} onChange={e => setDamageForm(p => ({ ...p, potholingMethod: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Vacuum Excavation">Vacuum Excavation</option>
            <option value="Hand Dig">Hand Dig</option>
            <option value="Not Performed">Not Performed</option>
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>Homes/businesses affected?</label>
          <input type="number" min="0" value={damageForm.homesAffected} onChange={e => setDamageForm(p => ({ ...p, homesAffected: e.target.value }))} placeholder="0" style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>City contacted?</label>
          <select value={damageForm.cityContacted} onChange={e => setDamageForm(p => ({ ...p, cityContacted: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="N/A">N/A</option>
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>Photos taken?</label>
          <select value={damageForm.photosTaken} onChange={e => setDamageForm(p => ({ ...p, photosTaken: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={submitDamageReport}
        disabled={formLoading}
        style={{
          ...getButtonStyle('primary', darkMode),
          width: '100%', marginTop: '20px', height: '48px', fontSize: '1rem',
          background: '#dc3545',
          cursor: formLoading ? 'not-allowed' : 'pointer',
          opacity: formLoading ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}
      >
        {formLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</> : <><AlertTriangle size={16} /> Submit Damage Report</>}
      </button>
    </div>
  );

  // ========== POTHOLE FORM ==========
  const renderPotholeForm = () => (
    <div style={{ ...cardStyle, borderTop: '3px solid #fd7e14' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MapPin size={22} color="#fd7e14" />
          <h3 style={{ margin: 0, color: tc.text }}>Pothole Verification</h3>
        </div>
        <button onClick={() => setActiveForm(null)} style={{ background: 'none', border: 'none', color: tc.textMuted, cursor: 'pointer', fontSize: '0.85rem' }}>
          Back
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
        <div>
          <label style={formLabelStyle}>Project<span style={requiredStar}>*</span></label>
          <input value={potholeForm.project} onChange={e => setPotholeForm(p => ({ ...p, project: e.target.value }))} placeholder="e.g. Bastrop Fiber" style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Address<span style={requiredStar}>*</span></label>
          <input value={potholeForm.address} onChange={e => setPotholeForm(p => ({ ...p, address: e.target.value }))} style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Cross Street</label>
          <input value={potholeForm.crossStreet} onChange={e => setPotholeForm(p => ({ ...p, crossStreet: e.target.value }))} style={formInputStyle} />
        </div>

        {/* GPS */}
        <div>
          <label style={formLabelStyle}>GPS Coordinates</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input value={potholeForm.gpsLat && potholeForm.gpsLng ? `${potholeForm.gpsLat}, ${potholeForm.gpsLng}` : ''} readOnly placeholder="Auto-capture" style={{ ...formInputStyle, flex: 1 }} />
            <button onClick={() => captureGPS(setPotholeForm)} disabled={gpsLoading} style={{ ...getButtonStyle('outline', darkMode), padding: '8px 12px', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
              {gpsLoading ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <MapPin size={14} />}
            </button>
          </div>
        </div>

        <div>
          <label style={formLabelStyle}>Depth</label>
          <input value={potholeForm.depth} onChange={e => setPotholeForm(p => ({ ...p, depth: e.target.value }))} placeholder='e.g. 36"' style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Surface Type</label>
          <select value={potholeForm.surfaceType} onChange={e => setPotholeForm(p => ({ ...p, surfaceType: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Concrete">Concrete</option>
            <option value="Asphalt">Asphalt</option>
            <option value="Gravel">Gravel</option>
            <option value="Dirt">Dirt</option>
            <option value="Grass/Sod">Grass/Sod</option>
          </select>
        </div>

        <div>
          <label style={formLabelStyle}>Utility Found?</label>
          <select value={potholeForm.utilityFound} onChange={e => setPotholeForm(p => ({ ...p, utilityFound: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        {/* Conditional: only show when utility found */}
        {potholeForm.utilityFound === 'Yes' && (
          <>
            <div>
              <label style={formLabelStyle}>Utility Type</label>
              <select value={potholeForm.utilityType} onChange={e => setPotholeForm(p => ({ ...p, utilityType: e.target.value }))} style={formSelectStyle}>
                <option value="">Select...</option>
                <option value="Gas">Gas</option>
                <option value="Electric">Electric</option>
                <option value="Water">Water</option>
                <option value="Sewer">Sewer</option>
                <option value="Telecom">Telecom</option>
                <option value="Fiber">Fiber</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label style={formLabelStyle}>Utility Depth</label>
              <input value={potholeForm.utilityDepth} onChange={e => setPotholeForm(p => ({ ...p, utilityDepth: e.target.value }))} placeholder='e.g. 24"' style={formInputStyle} />
            </div>
            <div>
              <label style={formLabelStyle}>Utility Direction</label>
              <select value={potholeForm.utilityDirection} onChange={e => setPotholeForm(p => ({ ...p, utilityDirection: e.target.value }))} style={formSelectStyle}>
                <option value="">Select...</option>
                <option value="North-South">North-South</option>
                <option value="East-West">East-West</option>
                <option value="NE-SW">NE-SW</option>
                <option value="NW-SE">NW-SE</option>
                <option value="Perpendicular to bore">Perpendicular to bore</option>
                <option value="Parallel to bore">Parallel to bore</option>
              </select>
            </div>
          </>
        )}

        <div>
          <label style={formLabelStyle}>Crew Lead<span style={requiredStar}>*</span></label>
          <input value={potholeForm.crewLead} onChange={e => setPotholeForm(p => ({ ...p, crewLead: e.target.value }))} style={formInputStyle} />
        </div>
        <div>
          <label style={formLabelStyle}>Photos Taken?</label>
          <select value={potholeForm.photosTaken} onChange={e => setPotholeForm(p => ({ ...p, photosTaken: e.target.value }))} style={formSelectStyle}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
      </div>

      <div style={{ marginTop: '4px' }}>
        <label style={formLabelStyle}>Notes</label>
        <textarea value={potholeForm.notes} onChange={e => setPotholeForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional details..." style={formTextareaStyle} />
      </div>

      <button
        onClick={submitPotholeVerification}
        disabled={formLoading}
        style={{
          ...getButtonStyle('primary', darkMode),
          width: '100%', marginTop: '20px', height: '48px', fontSize: '1rem',
          background: '#fd7e14',
          cursor: formLoading ? 'not-allowed' : 'pointer',
          opacity: formLoading ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}
      >
        {formLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</> : <><MapPin size={16} /> Submit Pothole Verification</>}
      </button>
    </div>
  );

  // ========== MATERIAL REQUEST FORM ==========
  const renderMaterialForm = () => {
    const urgencyColors = { Standard: '#4CAF50', Urgent: '#fd7e14', Emergency: '#dc3545' };

    return (
      <div style={{ ...cardStyle, borderTop: '3px solid #0d6efd' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Package size={22} color="#0d6efd" />
            <h3 style={{ margin: 0, color: tc.text }}>Material Request</h3>
          </div>
          <button onClick={() => setActiveForm(null)} style={{ background: 'none', border: 'none', color: tc.textMuted, cursor: 'pointer', fontSize: '0.85rem' }}>
            Back
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
          <div>
            <label style={formLabelStyle}>Project<span style={requiredStar}>*</span></label>
            <input value={materialForm.project} onChange={e => setMaterialForm(p => ({ ...p, project: e.target.value }))} placeholder="e.g. Bastrop Fiber" style={formInputStyle} />
          </div>
          <div>
            <label style={formLabelStyle}>Material Type<span style={requiredStar}>*</span></label>
            <select value={materialForm.materialType} onChange={e => setMaterialForm(p => ({ ...p, materialType: e.target.value }))} style={formSelectStyle}>
              <option value="">Select...</option>
              <option value="Conduit">Conduit</option>
              <option value="Fiber Cable">Fiber Cable</option>
              <option value="Handholes">Handholes</option>
              <option value="Splice Closures">Splice Closures</option>
              <option value="Drop Cable">Drop Cable</option>
              <option value="Tracer Wire">Tracer Wire</option>
              <option value="Pull Tape">Pull Tape</option>
              <option value="Innerduct">Innerduct</option>
              <option value="Restoration Materials">Restoration Materials</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
            <label style={formLabelStyle}>Description / Specs</label>
            <textarea value={materialForm.description} onChange={e => setMaterialForm(p => ({ ...p, description: e.target.value }))} placeholder='e.g. 1.25" HDPE SDR-11, orange' style={formTextareaStyle} />
          </div>
          <div>
            <label style={formLabelStyle}>Quantity<span style={requiredStar}>*</span></label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input type="number" min="0" value={materialForm.qty} onChange={e => setMaterialForm(p => ({ ...p, qty: e.target.value }))} placeholder="0" style={{ ...formInputStyle, flex: 1 }} />
              <select value={materialForm.unit} onChange={e => setMaterialForm(p => ({ ...p, unit: e.target.value }))} style={{ ...formSelectStyle, width: '90px', flex: 'none' }}>
                <option value="ft">ft</option>
                <option value="ea">ea</option>
                <option value="rolls">rolls</option>
                <option value="spools">spools</option>
                <option value="boxes">boxes</option>
                <option value="pallets">pallets</option>
              </select>
            </div>
          </div>
          <div>
            <label style={formLabelStyle}>Delivery Location</label>
            <input value={materialForm.deliveryLocation} onChange={e => setMaterialForm(p => ({ ...p, deliveryLocation: e.target.value }))} placeholder="Job site address or staging area" style={formInputStyle} />
          </div>
          <div>
            <label style={formLabelStyle}>Urgency</label>
            <select value={materialForm.urgency} onChange={e => setMaterialForm(p => ({ ...p, urgency: e.target.value }))} style={{
              ...formSelectStyle,
              borderColor: urgencyColors[materialForm.urgency] || tc.border,
              color: urgencyColors[materialForm.urgency] || tc.text,
              fontWeight: materialForm.urgency !== 'Standard' ? 600 : 400,
            }}>
              <option value="Standard">Standard (48 hrs)</option>
              <option value="Urgent">Urgent (24 hrs)</option>
              <option value="Emergency">Emergency (ASAP)</option>
            </select>
          </div>
          <div>
            <label style={formLabelStyle}>Needed By Date</label>
            <input type="date" value={materialForm.neededByDate} onChange={e => setMaterialForm(p => ({ ...p, neededByDate: e.target.value }))} style={formInputStyle} />
          </div>
        </div>

        <button
          onClick={submitMaterialRequest}
          disabled={formLoading}
          style={{
            ...getButtonStyle('primary', darkMode),
            width: '100%', marginTop: '20px', height: '48px', fontSize: '1rem',
            background: tc.gradientPortal,
            cursor: formLoading ? 'not-allowed' : 'pointer',
            opacity: formLoading ? 0.7 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
        >
          {formLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</> : <><Package size={16} /> Submit Material Request</>}
        </button>
      </div>
    );
  };

  // ========== DOCUMENTS SUB-TAB (existing compliance tracker) ==========
  const renderDocuments = () => {
    if (!complianceItems && !complianceLoading) loadComplianceData();

    const currentCount = complianceItems ? complianceItems.filter(c => c.status === 'Current' || c.status === 'On File' || c.status === 'Signed').length : 0;
    const totalCount = complianceItems ? complianceItems.length : 0;
    const pct = totalCount > 0 ? Math.round((currentCount / totalCount) * 100) : 0;

    return (
      <div>
        <h3 style={{ margin: '0 0 4px', color: tc.text, fontSize: '1.1rem' }}>Document Status</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: tc.textMuted }}>Track your insurance, certifications, and required documents</p>

        {/* Progress bar */}
        {complianceItems && complianceItems.length > 0 && (
          <div style={{ ...cardStyle, padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: tc.text }}>{currentCount} of {totalCount} items current</span>
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: pct === 100 ? tc.success : tc.accentPrimary }}>{pct}%</span>
            </div>
            <div style={{ height: '8px', borderRadius: '4px', background: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: '4px', background: pct === 100 ? tc.success : tc.gradientPortal, transition: 'width 0.5s ease' }} />
            </div>
          </div>
        )}

        <div style={cardStyle}>
          {complianceLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[1,2,3,4,5].map(i => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', backgroundColor: tc.cardBgAlt, borderRadius: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ width: '50%', height: '16px', borderRadius: '6px', background: 'linear-gradient(90deg, rgba(128,128,128,0.1) 25%, rgba(128,128,128,0.2) 50%, rgba(128,128,128,0.1) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', marginBottom: '6px' }} />
                    <div style={{ width: '30%', height: '12px', borderRadius: '6px', background: 'linear-gradient(90deg, rgba(128,128,128,0.1) 25%, rgba(128,128,128,0.2) 50%, rgba(128,128,128,0.1) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
                  </div>
                  <div style={{ width: '80px', height: '28px', borderRadius: '20px', background: 'linear-gradient(90deg, rgba(128,128,128,0.1) 25%, rgba(128,128,128,0.2) 50%, rgba(128,128,128,0.1) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
                </div>
              ))}
            </div>
          ) : complianceItems && complianceItems.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {complianceItems.map((doc, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '16px', backgroundColor: tc.cardBgAlt, borderRadius: '10px',
                  borderLeft: `3px solid ${doc.color}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {doc.status === 'EXPIRED' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: tc.error, animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
                    )}
                    <div>
                      <div style={{ fontWeight: '600', color: tc.text }}>{doc.label}</div>
                      <div style={{ fontSize: '0.8rem', color: tc.textMuted }}>
                        {doc.expires !== 'N/A' ? `Expires: ${doc.expires}` : ''}
                        {doc.daysLeft !== undefined && doc.daysLeft > 0 ? ` (${doc.daysLeft} days)` : ''}
                      </div>
                    </div>
                  </div>
                  <span style={{ padding: '4px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700, backgroundColor: `${doc.color}15`, color: doc.color }}>{doc.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Shield size={56} color={tc.textMuted} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <div style={{ color: tc.text, fontWeight: 500, marginBottom: '4px' }}>No compliance data found</div>
              <div style={{ fontSize: '0.85rem', color: tc.textMuted }}>Compliance data appears after you complete onboarding.</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== MAIN RENDER ==========
  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: tc.text }}>Compliance Hub</h2>
        <p style={{ color: tc.textMuted, margin: 0 }}>Requirements, reporting, and document tracking</p>
      </div>

      {renderSubTabs()}

      {/* Toast notification */}
      {formStatus.show && (
        <div style={{
          padding: '12px 20px', borderRadius: '8px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: formStatus.success ? (darkMode ? '#1b4332' : '#d4edda') : (darkMode ? '#3d1f1f' : '#f8d7da'),
          color: formStatus.success ? (darkMode ? '#95d5b2' : '#155724') : (darkMode ? '#ff6b6b' : '#721c24'),
          border: `1px solid ${formStatus.success ? (darkMode ? '#2d6a4f' : '#c3e6cb') : (darkMode ? '#5c2626' : '#f5c6cb')}`,
        }}>
          {formStatus.success ? <CheckCircle size={18} /> : <Info size={18} />}
          <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{formStatus.message}</span>
        </div>
      )}

      {subTab === 'requirements' && renderRequirements()}
      {subTab === 'forms' && renderForms()}
      {subTab === 'documents' && renderDocuments()}
    </div>
  );
};

export default ComplianceHub;
